/*
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../../../logger';
import { debugPixel, generateUUID, isGptAvailable } from '../../../util';
import { Placement } from '../../placement';
import { IAB_STANDARD_BANNER_SIZES } from '../../placement/constants';

import type { CampaignData } from '../../../types/';
import type { PrebidAdUnit } from '../../../types/prebid';
import type { SiteConfig } from '../../config';

const logger = Logger.getInstance();

class GptPlacement extends Placement {
  private interactionListenerAdded: boolean = false;
  private mouseEnteredIframe: boolean = false;
  private placementFrame: HTMLIFrameElement | null = null;
  private winBlurListener?: () => void;

  constructor(
    adUnit: PrebidAdUnit,
    adUnitTransformer: (adUnitCode: string) => string,
    campaignData: CampaignData[],
    siteConfig: SiteConfig
  ) {
    super(adUnit, adUnitTransformer, campaignData, siteConfig);
    logger.debug(`GPT Placement created for ${this.adUnitCode}`);
  }

  protected setSlot(): void {
    if (this.slot) {
      return;
    }
    if (isGptAvailable()) {
      logger.debug(`Determining GPT slot for adUnit: ${this.adUnitCode}`);
      try {
        const gptSlots = window.googletag.pubads().getSlots();

        const matchingSlot = gptSlots.find((slot) => {
          let slotElementId = slot.getSlotElementId();
          let slotAdUnitPath = slot.getAdUnitPath();
          logger.debug(`GPT slot available: ${slotAdUnitPath} (${slotElementId})`);
          return (
            this.adUnitCode == slotElementId ||
            this.adUnitCode == slotAdUnitPath ||
            slotAdUnitPath.endsWith(`/${this.adUnitCode}`)
          );
        });

        this.slot = matchingSlot || null;
        if (this.slot) {
          logger.info(`GPT slot located for ${this.adUnitCode}`);
        }
      } catch (e) {
        logger.error(`Error getting GPT slot for ${this.adUnitCode}: ${e}`);
        this.slot = null;
      }
    } else {
      logger.error(`GPT not available, unable to determine slot for adUnit: ${this.adUnitCode}`);
    }
  }

  protected setWrappingDomElement(): void {
    if (this.slot) {
      this.wrappingDomElement = document.getElementById(this.slot.getSlotElementId() as string);
    }
  }

  protected determineVisibility(): void {
    const size = this.getSmallestSupportedSize();
    if (!size || this.slot === null) {
      return;
    }

    let elementId: string;
    try {
      elementId = this.slot.getSlotElementId();
    } catch (e) {
      logger.error(`GPT Slot failed to return element ID: ${e}`);
      return;
    }

    const element = document.getElementById(elementId);
    if (!element) {
      logger.info(
        `GPT adunit '${this.adUnitCode}' Slot element reference '${elementId}' not found`
      );
      return;
    }

    const rect = element.getBoundingClientRect();
    const elementArea = size.width * size.height;

    if (elementArea === 0) {
      return;
    }

    const adLeft = rect.left + (rect.width - size.width) / 2;
    const adTop = rect.top + (rect.height - size.height) / 2;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const visibleWidth = Math.max(
      0,
      Math.min(adLeft + size.width, viewportWidth) - Math.max(adLeft, 0)
    );

    const visibleHeight = Math.max(
      0,
      Math.min(adTop + size.height, viewportHeight) - Math.max(adTop, 0)
    );

    const visibleArea = visibleWidth * visibleHeight;

    if (visibleArea >= elementArea / 2) {
      this.isVisible(true);
    } else {
      this.isVisible(false);
    }
  }

  protected setSupportedSizes(): void {
    const iabSizes = IAB_STANDARD_BANNER_SIZES.map((s) => {
      const [w, h] = s.split('x').map(Number);
      return { width: w, height: h };
    });
    try {
      this.slot.getSizes().forEach((size: any) => {
        // Only consider sizes that match a standard IAB banner size
        if (iabSizes.some((s) => s.width === size.width && s.height === size.height)) {
          this.addSupportedSize({ width: size.width, height: size.height });
        }
      });
    } catch (e) {
      logger.warn(`Error determining GPT slot size: ${e}`);
    }
  }

  protected addListeners(): void {
    logger.debug(`Adding GPT listeners for placement: ${this.adUnitCode}`);
    this.addSlotRenderedListener();
    this.addViewableImpressionListener();
    this.addPrebidAdRenderedListener();
  }

  private addSlotRenderedListener(): void {
    if (this.slot === null) {
      return;
    }
    try {
      logger.debug(`Adding slot render ended listener for GPT for: ${this.adUnitCode}`);
      const slotRenderedEventHandler = (event: googletag.events.SlotRenderEndedEvent) => {
        if (!this.slotRenderedForOpportunity && event.slot === this.slot) {
          this.interactionListenerAdded = false;
          this.slotRenderedForOpportunity = true;
          let deltaSeconds = Math.min(Math.round(this.currentOpportunity!.age / 1000), 20);
          debugPixel({
            en: 'gptSlotRendered',
            adUnitCode: this.adUnitCode,
            lineItemId: String(event.lineItemId),
            renderTimeDelta: String(deltaSeconds),
          });
          logger.debug(
            `GPT slot rendered for: ${event.slot.getAdUnitPath()},` +
              ` handled by placement (${this.adUnitCode}), line: ${event.lineItemId}`
          );

          const hasSize = Array.isArray(event.size) && event.size.length > 1;
          const renderedSlotDimensions = {
            // @ts-ignore
            width: hasSize ? (event.size[0] as number) : 0,
            // @ts-ignore
            height: hasSize ? (event.size[1] as number) : 0,
          };

          if (!event.isEmpty && !event.isBackfill && event.advertiserId && event.lineItemId) {
            const advertiserId = String(event.advertiserId);
            const creativeId = event.creativeId ? String(event.creativeId) : '';
            const lineId = String(event.lineItemId);
            const renderedAdData = {
              advertiserId: advertiserId,
              lineItemId: lineId,
              creativeId: creativeId,
              isProgrammaticBuy: false,
              responseIdentifier: event.responseIdentifier
                ? String(event.responseIdentifier)
                : generateUUID(),
              size: renderedSlotDimensions,
            };
            this.handleRenderedAdData(renderedAdData);
          } else {
            logger.debug(
              `GPT slot rendered backfill or empty: ${event.slot.getAdUnitPath()}, ${this.adUnitCode}`
            );
            if (event.isBackfill) {
              debugPixel({
                en: 'gptSlotRenderedBackfill',
                adUnitCode: this.adUnitCode,
                advertiserId: String(event.advertiserId),
              });
            } else if (event.isEmpty) {
              debugPixel({
                en: 'gptSlotReturnedEmpty',
                adUnitCode: this.adUnitCode,
              });
            }
            // Case of unlikely missing line ID not covered
          }
        }
      };

      window.googletag.pubads().addEventListener('slotRenderEnded', slotRenderedEventHandler);
    } catch (e) {
      logger.error(`Error adding GPT slotRenderEnded listener to ${this.adUnitCode}: ${e}`);
    }
  }

  private addViewableImpressionListener(): void {
    if (this.slot === null) {
      return;
    }
    try {
      const viewableImpressionEventHandler = (event: googletag.events.ImpressionViewableEvent) => {
        if (event.slot === this.slot) {
          logger.debug(
            `impressionViewable dispatched by GPT for: ${event.slot.getAdUnitPath()}, ${this.adUnitCode}`
          );
          this.dispatchViewableImpression();
        }
      };
      logger.debug(`Adding impressionViewable listener for GPT for: ${this.adUnitCode}`);
      window.googletag
        .pubads()
        .addEventListener('impressionViewable', viewableImpressionEventHandler);
    } catch (e) {
      logger.error(`Error adding GPT impressionViewable listener to ${this.adUnitCode}: ${e}`);
    }
  }

  protected addAdInteractionListener(): void {
    if (this.slot === null || this.interactionListenerAdded) {
      return;
    }
    logger.info(`Adding ad interaction listener for ${this.adUnitCode}`);
    const errorPrefix = `Unable to add ad interaction listener for ${this.adUnitCode}:`;
    const slotParent = document.getElementById(this.slot.getSlotElementId() as string);
    if (slotParent) {
      this.placementFrame = slotParent.querySelector('iframe');
      if (this.placementFrame) {
        try {
          const nativeWinOpen = this.placementFrame.contentWindow?.open;
          if (nativeWinOpen) {
            // @ts-ignore
            this.placementFrame.contentWindow.open = (...args) => {
              if (this.dispatchAdInteraction()) {
                debugPixel({
                  en: 'AdInteractionTriggerPlacementFrameWindowOpen',
                });
              }
              return nativeWinOpen.apply(this.placementFrame?.contentWindow, args);
            };
            // @ts-ignore
            this.placementFrame.contentWindow.addEventListener('focus', () => {
              if (this.dispatchAdInteraction()) {
                debugPixel({
                  en: 'AdInteractionTriggerFrameContentWindowFocus',
                });
              }
            });

            this.placementFrame.contentWindow?.document.addEventListener('click', () => {
              if (this.dispatchAdInteraction()) {
                debugPixel({
                  en: 'AdInteractionTriggerFrameContentWindowClick',
                });
              }
            });
            logger.debug(`Added focus and click listeners frame for: ${this.adUnitCode}`);
          }
        } catch (e) {
          logger.debug(
            `Unable to overwrite window opener for placement frame for ${this.adUnitCode}, error: ${e}`
          );
        } finally {
          this.addWindowBlurListeners();
        }
      } else {
        logger.warn(`${errorPrefix} ad iframe not found`);
      }
    } else {
      logger.warn(`${errorPrefix} iframe parent element not found`);
    }
    this.interactionListenerAdded = true;
  }

  private addWindowBlurListeners(): void {
    if (this.placementFrame) {
      const mouseEnterEventHandler = () => {
        this.mouseEnteredIframe = true;
      };
      const mouseLeaveEventHandler = () => {
        this.mouseEnteredIframe = false;
      };
      this.placementFrame.addEventListener('mouseenter', mouseEnterEventHandler);
      this.placementFrame.addEventListener('mouseleave', mouseLeaveEventHandler);
    }

    this.winBlurListener = () => {
      this.documentBlurHandler();
    };
    window.addEventListener('blur', this.winBlurListener);
  }

  private removeBlurEventListener(): void {
    if (this.winBlurListener) {
      window.removeEventListener('blur', this.winBlurListener);
    }
  }

  private documentBlurHandler(): void {
    if (!this.placementFrame) {
      return;
    }
    logger.info(`Processing blur event for ${this.adUnitCode}`);
    const logMsgPrefix = `Blur handler triggering interaction for ${this.adUnitCode} via `;

    // Direct iframe focus (works for cross-origin)
    if (document.activeElement === this.placementFrame) {
      logger.info(`${logMsgPrefix} active ele match.`);
      this.removeBlurEventListener();
      this.dispatchAdInteraction();
      debugPixel({
        en: 'AdInteractionTriggerBlurActiveElement',
      });
      return;
    }

    // Same-origin nested iframe detection
    try {
      const nestedDocument = this.placementFrame.contentWindow?.document as Document;
      const nestedIframes = [...nestedDocument.querySelectorAll('iframe')];

      if (this.mouseEnteredIframe && nestedIframes.length > 0) {
        logger.info(`${logMsgPrefix} nested frame becoming active.`);
        this.removeBlurEventListener();
        this.dispatchAdInteraction();
        debugPixel({
          en: 'AdInteractionTriggerNestedIframeActive',
        });
        return;
      }
    } catch (e) {}
  }
}

export { GptPlacement };
